import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, RefreshCw } from 'lucide-react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import './Broadcast.css';
import { clearBroadcastRecovery, readBroadcastRecovery, saveBroadcastRecovery } from './broadcastRecovery';
import { uhrzeit } from '../lib/zeit';

const INITIAL = { target: 'drivers', status: 'all', partner_id: null, customer_ids: [], template_id: null, title: '', message: '' };
const TARGETS = { drivers: 'Fahrer', customers: 'Kunden', partners: 'Partner', all: 'Fahrer und Kunden' };
const STATUSES = { pending: 'Wartend', sending: 'In Bearbeitung', sent: 'Vom Push-Dienst angenommen', failed: 'Fehlgeschlagen', no_recipient: 'Kein registriertes Push-Gerät', skip: 'Übersprungen' };
const newKey = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(24));
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};
const errorText = (error, fallback) => error?.response?.data?.message || fallback;

function CompletionAutomation() {
  const [rule, setRule] = useState(null);
  const [draft, setDraft] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(false);
  const [details, setDetails] = useState(false);
  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/admin/broadcast/automation/completion');
      setRule(data.rule); setDraft(data.rule); setStatuses(data.status || []);
    } catch { setError('Automatische Nachrichten konnten nicht geladen werden.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);
  const save = async (enabled = draft.enabled) => {
    if (!draft || busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const { data } = await api.put('/admin/broadcast/automation/completion', { ...draft, enabled });
      setRule(data.rule); setDraft(data.rule); setEditing(false);
      setNotice(enabled ? 'Regel aktiv. Sie gilt nur für neue Fahrtabschlüsse.' : 'Regel pausiert. Offene Nachrichten werden nicht versendet.');
    } catch (e) { setError(errorText(e, 'Regel konnte nicht gespeichert werden. Bitte erneut prüfen.')); }
    finally { setBusy(false); }
  };
  const describe = item => item.push_status === 'sent' ? 'Vom Push-Dienst angenommen'
    : item.push_status === 'failed' ? 'Fehlgeschlagen'
      : item.push_status === 'no_recipient' ? 'Kein Push-Gerät'
        : item.push_status === 'skip' ? 'Übersprungen'
          : item.push_status === 'sending' ? 'In Bearbeitung' : 'Geplant';
  return <section className="broadcast-card broadcast-automation" aria-label="Automatische Nachrichten">
    <h2>Automatische Nachrichten</h2>
    <p>Nachricht nach Fahrtabschluss. Neue Regeln starten pausiert; das Speichern verschickt nichts rückwirkend.</p>
    {loading && <p role="status">Regel wird geladen …</p>}
    {error && <p role="alert" className="broadcast-error">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {rule && !editing && <div className="broadcast-automation-summary">
      <strong>{rule.name}</strong><span>{rule.enabled ? 'Aktiv' : 'Pausiert'}</span>
      <p>Der Kunde erhält {rule.delay_minutes === 0 ? 'direkt' : `${rule.delay_minutes} Minuten`} nach dem erfolgreichen Abschluss seiner neuen Fahrt eine Nachricht.</p>
      <button type="button" onClick={() => { setDraft(rule); setEditing(true); setNotice(''); }}>Bearbeiten</button>
      {rule.enabled && <button type="button" disabled={busy} onClick={() => save(false)}>Pausieren</button>}
    </div>}
    {editing && draft && <div className="broadcast-automation-edit">
      <label htmlFor="auto-name">Regelname</label>
      <input id="auto-name" maxLength={80} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
      <label htmlFor="auto-delay">Nach wie vielen Minuten?</label>
      <input id="auto-delay" type="number" min={0} max={1440} value={draft.delay_minutes} onChange={e => setDraft({ ...draft, delay_minutes: e.target.value })} />
      <label htmlFor="auto-title">Push-Titel</label>
      <input id="auto-title" maxLength={120} value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} />
      <label htmlFor="auto-message">Nachricht</label>
      <textarea id="auto-message" maxLength={2000} rows={4} value={draft.message} onChange={e => setDraft({ ...draft, message: e.target.value })} />
      <div className="broadcast-message-preview"><strong>{draft.title || 'Titel deiner Nachricht'}</strong><p>{draft.message || 'Hier erscheint deine Nachricht.'}</p></div>
      <p>Kunden erhalten diese Werbenachricht nach Fahrtabschluss nur mit gültiger Marketing-Einwilligung. Ein Widerruf vor dem Versand verhindert die Zustellung. Bestehende Fahrtabschlussmeldung bleibt getrennt.</p>
      <div className="broadcast-automation-actions">
        <button type="button" disabled={busy} onClick={() => save(false)}>Als Entwurf speichern</button>
        <button type="button" disabled={busy || !draft.title.trim() || !draft.message.trim()} onClick={() => save(true)}>Speichern und aktivieren</button>
        <button type="button" disabled={busy} onClick={() => { setDraft(rule); setEditing(false); setError(''); }}>Abbrechen</button>
      </div>
    </div>}
    <button type="button" onClick={() => setDetails(!details)}>{details ? 'Versanddetails ausblenden' : 'Versanddetails anzeigen'}</button>
    {details && <div className="broadcast-automation-status">
      <button type="button" disabled={loading} onClick={load}>Status aktualisieren</button>
      {statuses.length ? <ul>{statuses.map(item => <li key={item.outbox_id}>{new Date(item.due_at_ms).toLocaleString('de-DE')}: {describe(item)} · Regelversion {item.rule_version}</li>)}</ul>
        : <p>Noch keine neuen Fahrtabschlüsse für diese Regel.</p>}
      <small>„Angenommen“ bedeutet nicht, dass die Nachricht auf dem Gerät angezeigt oder gelesen wurde.</small>
    </div>}
  </section>;
}

export default function Broadcast({ accountId }) {
  const [recovery] = useState(() => readBroadcastRecovery(accountId));
  const [draft, setDraft] = useState(() => recovery ? Object.fromEntries(Object.keys(INITIAL).map(key => [key, recovery[key]])) : INITIAL);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [customerMode, setCustomerMode] = useState(recovery?.customer_ids?.length ? 'selected' : 'all');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState(() => (recovery?.customer_ids || []).map(id => ({ id, label: id })));
  const [partner, setPartner] = useState(recovery?.partner_id ? { id: recovery.partner_id, label: recovery.partner_id } : null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(recovery);
  const [batch, setBatch] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [error, setError] = useState(recovery ? 'Ein unklarer Versandauftrag wurde wiederhergestellt. Bitte denselben Auftrag erneut prüfen.' : '');
  const revision = useRef(0);
  const sending = useRef(false);
  const optionsTarget = draft.target === 'drivers' ? 'partners' : draft.target === 'customers' && customerMode === 'selected' ? 'customers' : null;
  const locked = Boolean(attempt || batch);

  const loadCatalog = () => {
    setCatalogLoading(true);
    setCatalogError('');
    api.get('/admin/broadcast/catalog').then(r => setCatalog(r.data.templates || []))
      .catch(() => setCatalogError('Vorlagen konnten nicht geladen werden. Freitext bleibt möglich.'))
      .finally(() => setCatalogLoading(false));
  };
  useEffect(loadCatalog, []);

  useEffect(() => {
    let current = true;
    setOptions([]);
    setOptionsError('');
    if (!optionsTarget || (optionsTarget === 'customers' && query.trim().length < 2)) {
      setOptionsLoading(false);
      return () => { current = false; };
    }
    setOptionsLoading(true);
    const timer = setTimeout(() => {
      api.get('/admin/broadcast/options', { params: { target: optionsTarget, q: query.trim() } })
        .then(r => { if (current) setOptions(r.data.items || []); })
        .catch(() => { if (current) setOptionsError('Empfänger konnten nicht geladen werden. Bitte Suche erneut eingeben.'); })
        .finally(() => { if (current) setOptionsLoading(false); });
    }, 250);
    return () => { current = false; clearTimeout(timer); };
  }, [optionsTarget, query]);

  useEffect(() => {
    if (!preview) return undefined;
    const delay = new Date(preview.expires_at).getTime() - Date.now();
    const timer = setTimeout(() => setPreview(null), Math.max(0, delay));
    return () => clearTimeout(timer);
  }, [preview]);

  const edit = patch => {
    revision.current += 1;
    setDraft(d => ({ ...d, ...patch }));
    setPreview(null);
    setError('');
  };
  const chooseTarget = target => {
    edit({ target, status: 'all', partner_id: null, customer_ids: [], template_id: null });
    setCustomerMode('all'); setSelectedCustomers([]); setPartner(null); setQuery('');
  };
  const chooseTemplate = id => {
    if (!id) { edit({ template_id: null }); return; }
    const template = catalog.find(t => t.id === id);
    if (!template || !TARGETS[template.target]) return;
    edit({ ...INITIAL, target: template.target, status: template.status || 'all', template_id: template.id, title: template.title, message: template.message });
    setCustomerMode('all'); setSelectedCustomers([]); setPartner(null); setQuery('');
  };
  const valid = draft.title.trim() && draft.message.trim()
    && !(draft.target === 'customers' && customerMode === 'selected' && !draft.customer_ids.length)
    && !(draft.template_id === 'partner_driver_online' && !draft.partner_id);

  const makePreview = async event => {
    event.preventDefault();
    if (!valid || busy || locked) return;
    const version = revision.current;
    setBusy(true); setError(''); setPreview(null);
    try {
      const { data } = await api.post('/admin/broadcast/preview', draft);
      if (version === revision.current) {
        if (!data.preview_token || !(data.recipient_count > 0) || !(new Date(data.expires_at).getTime() > Date.now())) {
          setError('Keine gültige Empfängervorschau erhalten. Bitte erneut prüfen.');
        } else setPreview(data);
      }
    } catch (e) { if (version === revision.current) setError(errorText(e, 'Vorschau nicht verfügbar. Bitte erneut prüfen.')); }
    finally { setBusy(false); }
  };
  const refreshBatch = async id => {
    setBatchLoading(true); setBatchError('');
    try { const { data } = await api.get(`/admin/broadcast/${encodeURIComponent(id)}`); setBatch(data); }
    catch { setBatchError('Versandauftrag gespeichert. Der aktuelle Verarbeitungsstand ist nicht verfügbar. Bitte aktualisieren.'); }
    finally { setBatchLoading(false); }
  };
  const send = async () => {
    if (sending.current || batch || (!attempt && (!preview || new Date(preview.expires_at).getTime() <= Date.now()))) return;
    let request;
    try {
      request = attempt || { ...draft, preview_token: preview.preview_token, idempotency_key: newKey() };
      saveBroadcastRecovery(accountId, request);
    } catch {
      setError('Der Versandauftrag kann nicht sicher zwischengespeichert werden. Bitte Browser-Sitzung und Anmeldung prüfen. Es wurde kein neuer Versand gestartet.');
      return;
    }
    sending.current = true; setBusy(true); setAttempt(request); setError('');
    try {
      const { data } = await api.post('/admin/broadcast/send', request);
      clearBroadcastRecovery();
      setBatch(data); setAttempt(null); setPreview(null);
      await refreshBatch(data.id);
    } catch (e) {
      // A missing response or server failure may follow a committed request. Keep
      // the exact request/key and lock editing until its outcome is reconciled.
      if (e.response && e.response.status >= 400 && e.response.status < 500 && e.response.status !== 408 && e.response.status !== 429) {
        clearBroadcastRecovery();
        setAttempt(null); setPreview(null);
        setError(e.response.status === 409 ? 'Vorschau oder Empfänger haben sich geändert. Bitte erneut prüfen; dieser Aufruf wurde nicht neu versendet.' : errorText(e, 'Versand abgelehnt. Bitte Angaben und Berechtigung prüfen.'));
      } else setError('Versandstatus unklar. Bitte denselben Auftrag erneut prüfen. Der Wiederholungsversuch verwendet dieselbe Auftragskennung.');
    } finally { sending.current = false; setBusy(false); }
  };

  return (
    <div className="broadcast-page">
      <PageHeader title="Nachrichten senden" description="Empfänger auswählen, Nachricht prüfen und den Versand ausdrücklich bestätigen." actions={<Link to="/support">Zum Support-Postfach</Link>} />
      <CompletionAutomation />
      <div className="broadcast-layout">
        <form onSubmit={makePreview} className="broadcast-card">
          <fieldset disabled={locked}>
            <legend>1. Empfänger und Nachricht</legend>
            <label htmlFor="broadcast-template">Vorlage</label>
            <select id="broadcast-template" value={draft.template_id || ''} onChange={e => chooseTemplate(e.target.value)}>
              <option value="">Freitext</option>
              {catalog.filter(t => TARGETS[t.target]).map(t => <option value={t.id} key={t.id}>{t.label}</option>)}
            </select>
            {catalogLoading && <p role="status">Vorlagen werden geladen …</p>}
            {catalogError && <p role="alert">{catalogError} <button type="button" onClick={loadCatalog}>Vorlagen erneut laden</button></p>}
            <label htmlFor="broadcast-target">Empfängergruppe</label>
            <select id="broadcast-target" value={draft.target} onChange={e => chooseTarget(e.target.value)}>
              {Object.entries(TARGETS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            {draft.target === 'all' && <p>Diese Auswahl umfasst Fahrer und Kunden. Partnerkonten werden separat ausgewählt.</p>}
            {draft.target === 'partners' && <p>Alle Partnerkonten. Fahrer der Partner sind eine eigene Empfängergruppe.</p>}
            {draft.target === 'drivers' && <>
              <label htmlFor="broadcast-status">Fahrer-Status</label>
              <select id="broadcast-status" value={draft.status} onChange={e => edit({ status: e.target.value })}>
                <option value="all">Alle Fahrer</option><option value="online">Online</option><option value="offline">Offline</option>
              </select>
              <p>Es zählt der bestätigte Dienststatus. Eine Netzwerkverbindung allein bedeutet nicht „online“.</p>
              <label htmlFor="broadcast-partner">Fahrer eines Partners (optional)</label>
              {partner ? <div className="broadcast-selection">{partner.label || partner.name}<button type="button" onClick={() => { setPartner(null); edit({ partner_id: null }); }}>Partnerauswahl entfernen</button></div> : <p>Alle Partner und Fahrer ohne Partnerzuordnung</p>}
              <input id="broadcast-partner" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Partner suchen" />
              {draft.template_id === 'partner_driver_online' && !draft.partner_id && <p role="status">Für diese Vorlage bitte einen Partner auswählen.</p>}
            </>}
            {draft.target === 'customers' && <>
              <label htmlFor="broadcast-customer-mode">Kundenauswahl</label>
              <select id="broadcast-customer-mode" value={customerMode} onChange={e => { setCustomerMode(e.target.value); setSelectedCustomers([]); setQuery(''); edit({ customer_ids: [] }); }}>
                <option value="all">Alle Kunden</option><option value="selected">Einzelne oder mehrere Kunden</option>
              </select>
              {customerMode === 'selected' && <>
                <label htmlFor="broadcast-customer">Kunden suchen (mindestens 2 Zeichen)</label>
                <input id="broadcast-customer" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Name oder Telefonnummer" />
                <ul className="broadcast-selected" aria-label="Ausgewählte Kunden">{selectedCustomers.map(c => <li key={c.id}>{c.label || c.name}<button type="button" aria-label={`${c.label || c.name} entfernen`} onClick={() => { const next = selectedCustomers.filter(x => x.id !== c.id); setSelectedCustomers(next); edit({ customer_ids: next.map(x => x.id) }); }}>Entfernen</button></li>)}</ul>
                {!selectedCustomers.length && <p>Bitte mindestens einen Kunden auswählen.</p>}
              </>}
            </>}
            {optionsTarget && <>
              {optionsLoading && <p role="status">Empfänger werden gesucht …</p>}
              {optionsError && <p role="alert">{optionsError}</p>}
              {!optionsLoading && !optionsError && !options.length && (optionsTarget === 'partners' || query.trim().length >= 2) && <p>Keine Treffer. Suche bei Bedarf verfeinern.</p>}
              <ul className="broadcast-options" aria-label={optionsTarget === 'partners' ? 'Partner-Suchergebnisse' : 'Kunden-Suchergebnisse'}>{options.map(item => <li key={item.id}><button type="button" disabled={optionsTarget === 'customers' && draft.customer_ids.includes(item.id)} onClick={() => {
                if (optionsTarget === 'partners') { setPartner(item); edit({ partner_id: item.id }); }
                else { const next = [...selectedCustomers, item]; setSelectedCustomers(next); edit({ customer_ids: next.map(c => c.id) }); }
              }}>{item.label || item.name}{optionsTarget === 'customers' && draft.customer_ids.includes(item.id) ? ' · Ausgewählt' : ''}</button></li>)}</ul>
            </>}
            <label htmlFor="broadcast-title">Titel</label>
            <input id="broadcast-title" required maxLength={120} value={draft.title} onChange={e => edit({ title: e.target.value })} />
            <label htmlFor="broadcast-message">Nachricht</label>
            <textarea id="broadcast-message" required maxLength={2000} rows={6} value={draft.message} onChange={e => edit({ message: e.target.value })} />
            <p>Vorlagen sind frei bearbeitbar. Bitte keine vertraulichen Informationen in Push-Nachrichten aufnehmen.</p>
          </fieldset>
          <button className="broadcast-primary" type="submit" disabled={!valid || busy || locked}>{busy && !attempt ? 'Wird geprüft …' : 'Empfänger und Nachricht prüfen'}</button>
        </form>
        <section className="broadcast-card broadcast-preview" aria-label="Vorschau und Versand">
          <h2>2. Prüfen und senden</h2>
          <div className="broadcast-message-preview"><strong>{draft.title || 'Titel deiner Nachricht'}</strong><p>{draft.message || 'Hier erscheint deine Nachricht.'}</p></div>
          <p>{TARGETS[draft.target]}{draft.target === 'drivers' ? ` · ${draft.status === 'all' ? 'Alle Status' : draft.status === 'online' ? 'Online' : 'Offline'} · ${partner ? partner.label || partner.name : 'Alle Partner'}` : ''}{draft.target === 'customers' ? customerMode === 'all' ? ' · Alle Kunden' : ` · ${draft.customer_ids.length} ausgewählt` : ''}</p>
          {preview && <div className="broadcast-count" role="status"><strong>{preview.recipient_count} Empfänger</strong><p>{preview.breakdown?.drivers || 0} Fahrer · {preview.breakdown?.customers || 0} Kunden · {preview.breakdown?.partners || 0} Partner</p><small>Vorschau gültig bis {uhrzeit(preview.expires_at, { sekunden: true })}. Änderungen erfordern eine neue Prüfung.</small></div>}
          {!preview && !attempt && !batch && <p>Prüfe zuerst die aktuelle Empfängerzahl. Dabei wird noch nichts versendet.</p>}
          {error && <p role="alert" className="broadcast-error">{error}</p>}
          {!batch && <button type="button" className="broadcast-primary" disabled={busy || (!preview && !attempt)} onClick={send}><Send size={16} aria-hidden="true" />{busy && attempt ? 'Auftrag wird geprüft …' : attempt ? 'Denselben Versandauftrag erneut prüfen' : `Jetzt${preview ? ` an ${preview.recipient_count} Empfänger` : ''} senden`}</button>}
          {batch && <div className="broadcast-batch" role="status">
            <h3>Versandauftrag gespeichert</h3><p>{batch.queued} Push-Aufträge eingereiht.</p>
            {batch.inapp_saved != null && <p>{batch.inapp_saved} Nachrichten im Postfach gespeichert.</p>}
            <p className="broadcast-batch-id">Auftrag: {batch.id}</p>
            {batch.status && <dl>{Object.entries(STATUSES).map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{batch.status[key] ?? '—'}</dd></div>)}</dl>}
            {batchError && <p role="alert">{batchError}</p>}
            <button type="button" disabled={batchLoading} onClick={() => refreshBatch(batch.id)}><RefreshCw size={15} aria-hidden="true" />{batchLoading ? 'Wird aktualisiert …' : 'Status aktualisieren'}</button>
            <button type="button" disabled={busy} onClick={() => { setBatch(null); setPreview(null); setError(''); }}>Weitere Nachricht vorbereiten</button>
          </div>}
          <p className="broadcast-note">„Vom Push-Dienst angenommen“ bestätigt die Annahme durch den Versanddienst. Zustellung, Anzeige oder Lesen auf dem Gerät sind damit nicht bestätigt.</p>
        </section>
      </div>
    </div>
  );
}
